// rally 타입을 사람이 읽는 글자로 — **두 화면이 같은 표를 읽는다**
//
// ── 왜 화면 밖에 있는가 ────────────────────────────────────────────────────
//
// 이 글자는 작전 구성 쪽 둘(rally 폼, 시퀀스 목록)과 연맹원 쪽 하나(열람 카드)에
// 함께 뜬다. 그리고 그 둘은 **컴포넌트를 공유하지 않는다** — 놓이는 자리도 크기도 다르고,
// 한쪽을 되돌릴 때 다른 쪽이 함께 무너지지 않게 하려고 일부러 갈라 두었다.
//
// 갈라 둔 대가가 2026-08-17 까지 실제로 있었다. 표식 문자 표(`{ main: 'M', … }`)가
// `compose/rally/form-attack.tsx` 와 `view-attack/cards.tsx` 두 곳에 **똑같이 두 번** 적혀
// 있었다. 색 표가 두 벌인 것은 옳다 — 한쪽은 테두리고 한쪽은 글자색이라 시각 처리가 애초에
// 다르다. 그러나 **글자는 같아야 하는 것**이고, 같아야 하는 것을 두 벌로 적으면 갈리는 날이
// 온다. `src/i18n/index.ts` 가 공용 네임스페이스를 만든 근거가 정확히 이것이다.
//
// ── 왜 상수가 아니라 카탈로그인가 ──────────────────────────────────────────
//
// 옛 코드는 타입 이름을 `{candidate}` 로 **소스의 값 그대로** 찍었다. 화면에 뜨는 글자가
// 코드의 식별자와 같은 문자열이면 편하지만, 그 순간 그 글자는 번역될 수 없다 — 바꾸면 타입도
// 함께 바뀐다. 값과 글자를 가르는 것이 이 파일이 하는 일의 전부다.
//
// **지금 두 언어의 값이 같다.** 정본 7.2 가 게임 도메인 용어는 각 언어 게임 클라이언트의
// 공식 표기를 그대로 쓰라고 계약했고, 사용자가 2026-08-17 에 확인해 준 표에서 이 셋은
// 영어 클라이언트의 낱말이다. 값이 같다고 배선이 쓸모없는 것이 아니다 — 정본은 다섯 언어를
// 목표로 적었고, 그때 고칠 자리가 카탈로그 한 줄이어야 한다.
//
// 색은 여기 없다. 색은 두 화면이 서로 다르게 처리하는 것이라 각자의 파일에 남는다.

import type { RallyKind } from '../../core/timing/attack.ts'
import type { CommonStrings } from '../../i18n/index.ts'

/**
 * 타입 이름. **버튼 라벨과 카드의 `aria-label` 이 이것을 쓴다.**
 *
 * 화면에 안 보이는 `aria-label` 도 사람이 읽는 글자다. 스크린 리더로 이 화면을 듣는
 * 사람에게는 그것이 **유일한** 타입 표시라, 여기만 소스의 식별자로 남겨 두면 그 사람에게는
 * 아무것도 번역되지 않은 셈이 된다.
 */
export function kindLabel(kind: RallyKind, t: CommonStrings): string {
  if (kind === 'main') return t.kindMain
  if (kind === 'strike') return t.kindStrike
  return t.kindGarrison
}

/**
 * 타입 표식 한 글자. **색 단독으로 뜻을 지지 않게 하는 장치다.**
 *
 * 지금 값은 영어 낱말의 머리글자(main·strike·garrison)라 라틴 문자를 안 쓰는 언어에서는
 * 다른 글자가 된다. 그래서 라벨에서 첫 글자를 잘라 만들지 않고 따로 둔다 — 자르면 그 언어의
 * 관례를 앱이 대신 정해 버리고, 정해 버린 것은 카탈로그에서 못 고친다.
 */
export function kindMark(kind: RallyKind, t: CommonStrings): string {
  if (kind === 'main') return t.kindMarkMain
  if (kind === 'strike') return t.kindMarkStrike
  return t.kindMarkGarrison
}
